import { useCallback, useEffect, useState } from 'react'
import { Calendar, Plus, Loader2, ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api'
import EventFormModal from '../calendar/EventFormModal'
import {
  CalendarEventTypeColor,
  CalendarEventTypeLabel,
  type CalendarEventDto,
  type CalendarListResponse,
} from '@toga/shared'

interface Props {
  caseId: string
  canWrite: boolean
}

export default function CaseCalendarPanel({ caseId, canWrite }: Props) {
  const [events, setEvents] = useState<CalendarEventDto[] | null>(null)
  const [showNew, setShowNew] = useState(false)
  const [editing, setEditing] = useState<CalendarEventDto | null>(null)

  const load = useCallback(async () => {
    try {
      // Próximos 12 meses
      const from = new Date()
      const to = new Date()
      to.setMonth(to.getMonth() + 12)
      const params = new URLSearchParams({
        from: from.toISOString(),
        to: to.toISOString(),
        caseId,
      })
      const resp = await api.get<CalendarListResponse>(`/calendar-events?${params.toString()}`)
      setEvents(resp.items)
    } catch {
      setEvents([])
    }
  }, [caseId])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <div className="surface p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="t-primary font-semibold flex items-center gap-2">
          <Calendar size={16} className="text-toga-300" />
          Próximos eventos
          {events && <span className="text-xs t-muted font-normal">({events.length})</span>}
        </h3>
        {canWrite && (
          <button
            type="button"
            onClick={() => setShowNew(true)}
            className="px-2.5 py-1 rounded-lg text-xs font-medium text-toga-200 bg-toga-500/15 hover:bg-toga-500/25 flex items-center gap-1"
          >
            <Plus size={14} /> Nuevo
          </button>
        )}
      </div>

      {!events ? (
        <div className="t-muted text-sm flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Cargando…
        </div>
      ) : events.length === 0 ? (
        <p className="t-muted text-sm">Sin eventos planificados.</p>
      ) : (
        <ul className="space-y-1.5">
          {events.slice(0, 10).map((e) => {
            const color = CalendarEventTypeColor[e.type]
            const start = new Date(e.startAt)
            return (
              <li key={e.id}>
                <button
                  type="button"
                  onClick={() => setEditing(e)}
                  className="w-full text-left flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-hover/50"
                >
                  <span className={`w-1.5 h-8 rounded-full ${color.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm t-primary truncate">{e.title}</div>
                    <div className="text-xs t-faint">
                      {start.toLocaleString('es-ES', {
                        weekday: 'short',
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}{' '}
                      · {CalendarEventTypeLabel[e.type]}
                    </div>
                  </div>
                  <ChevronRight size={14} className="t-faint" />
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <div className="mt-3 text-right">
        <Link
          to="/agenda"
          className="text-xs text-toga-300 hover:underline inline-flex items-center gap-1"
        >
          Ver agenda completa <ChevronRight size={12} />
        </Link>
      </div>

      {(showNew || editing) && (
        <EventFormModal
          initial={editing}
          defaultCaseId={caseId}
          onClose={() => {
            setShowNew(false)
            setEditing(null)
          }}
          onSaved={() => {
            setShowNew(false)
            setEditing(null)
            void load()
          }}
        />
      )}
    </div>
  )
}
